import {zodResolver} from '@hookform/resolvers/zod';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, {Fragment, useEffect, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';

import {Snackbar} from 'presentation/components';
import {AlertModal} from 'presentation/components/AlertModal';
import {Loading} from 'presentation/components/Loading/Loading';
import {translations} from 'presentation/translations-new';

import {OperationStateTypesEnum} from 'presentation/constants';
import {VALIDATION_CODE_NAME, useSteps, useVerification} from 'presentation/pages/public/hooks';
import {StyledFixedFooter} from 'presentation/pages/public/styles';

import {useMediaQueries} from 'presentation/pages/private';
import {LoginEnum} from 'main/analytics/gaTagsEnum';
import {useForgotPasswordData} from '../../hooks/useForgotPasswordData';
import {useResendTimer} from '../../hooks/useResendTimer';
import {useResendToken} from '../../hooks/useResendToken';
import {useVerifyToken} from '../../hooks/useVerifyToken';
import {setSnackbarTrigger} from '../../hooks/useSteps';
import {ResendTokenContent} from './components/ResendTokenPage/ResendTokenContent';
import {IFormData, formSchema} from './schema';
import {StyledInput} from './styles';
import {CodeValidationFormProps} from './types';
import {PublicFlowEnum} from '../../constants/PublicFlowEnum';
import {formatCountdownMmSs} from 'presentation/utils/formatCountdownMmSs';

const {formLabels, actionText, resendToken} = translations['pt-br'].publicFlow;
const {
	codeDoNotSend,
	needHelp,
	sendCode,
	sendCodeToNumber,
	sendCodeToEmail,
	sendCodeToNumberAndEmail,
	invalidCode,
} = translations['pt-br'].publicFlow.forgotPassword;
const {typeSecurityCode, resendSecurityCode, mobileSecurityCodeTitle} =
	translations['pt-br'].privateFlow.profile.changePassword;

const VERIFICATION_CODE_LENGTH = 7;

export function CodeValidationForm({flowType, privateFlow}: CodeValidationFormProps) {
	const [showPrivateResendToken, setShowPrivateResendToken] = useState(false);
	const {timerData, resetTimer} = useResendTimer();
	const [currentTimer, setCurrentTimer] = useState(timerData.timer);
	const {handleHelpStep} = useSteps(PublicFlowEnum.FORGOT_PASSWORD);
	const {data: forgotPasswordData} = useForgotPasswordData();
	const [savedContactInfo, setSavedContactInfo] = useState({
		telefoneMascarado: null,
		emailMascarado: null,
	});

	const {isLaptop} = useMediaQueries();

	const methods = useForm<IFormData>({
		defaultValues: {
			code1: '',
			code2: '',
			code3: '',
			code4: '',
			code5: '',
			code6: '',
			code7: '',
		},
		mode: 'onBlur',
		resolver: zodResolver(formSchema),
	});

	const {
		register,
		handleSubmit,
		formState: {isSubmitting},
		getValues,
	} = methods;

	const {inputClass, handleChange, handleKeyDown, handlePaste} = useVerification({
		codeLength: VERIFICATION_CODE_LENGTH,
		methods,
	});

	const {
		handleVerifyToken,
		handleVerifyTokenPrivate,
		handleGetCdPessoaPrivate,
		showAPIError: showVerifyTokenAPIError,
		apiErrorMsg,
		openRateLimitModal,
		rateLimitRetryAfter,
		rateLimitModalWarning,
		rateLimitExceededTitle,
		rateLimitExceededDescription,
		retryAgainIn,
		handleCloseRateLimitModal,
		resetRateLimitState,
	} = useVerifyToken({methods, flowType});

	const [showEmptyCodeError, setShowEmptyCodeError] = useState(false);
	const [rateLimitCountdown, setRateLimitCountdown] = useState(0);

	const {
		requestState,
		handleResendToken,
		handleCloseSnackbar,
		handleOpenSnackbar,
		openSnackbar,
		clearAPIError: clearResendAPIError,
		showAPIError: showResendTokenAPIError,
		apiErrorMsg: resendTokenErrorMsg,
	} = useResendToken({
		methods,
		flowType,
	});

	function handleVerifyTokenSubmit(data: IFormData) {
		const codeValues = Object.values(data);
		const isCodeIncompleteOrEmpty = codeValues.some(
			value => value === '' || value === null || value === undefined,
		);

		if (isCodeIncompleteOrEmpty) {
			setShowEmptyCodeError(true);
			return;
		}

		setShowEmptyCodeError(false);

		if (privateFlow) {
			handleVerifyTokenPrivate(data);
		} else {
			handleVerifyToken(data);
		}
		clearResendAPIError();
	}

	const handleButtonClick = () => {
		const currentValues = getValues();
		const codeValues = Object.values(currentValues);
		const isCodeEmpty = codeValues.every(
			value => value === '' || value === null || value === undefined,
		);
		const isCodeIncomplete = codeValues.some(
			value => value === '' || value === null || value === undefined,
		);

		if (isCodeEmpty || isCodeIncomplete) {
			setShowEmptyCodeError(true);
			return;
		}

		handleSubmit(handleVerifyTokenSubmit)();
	};

	useEffect(() => {
		if (
			forgotPasswordData?.telefoneMascarado !== undefined ||
			forgotPasswordData?.emailMascarado !== undefined
		) {
			setSavedContactInfo({
				telefoneMascarado: forgotPasswordData.telefoneMascarado,
				emailMascarado: forgotPasswordData.emailMascarado,
			});
		}
	}, [forgotPasswordData?.telefoneMascarado, forgotPasswordData?.emailMascarado]);

	useEffect(() => {
		if (privateFlow) {
			handleGetCdPessoaPrivate();
		}
	}, []);

	useEffect(() => {
		setSnackbarTrigger(() => {
			handleOpenSnackbar();
		});
	}, [handleOpenSnackbar]);

	useEffect(() => {
		const elapsed = Math.floor((Date.now() - timerData.startTime) / 1000);
		const remainingTime = Math.max(0, timerData.timer - elapsed);
		setCurrentTimer(remainingTime);
	}, [timerData]);

	useEffect(() => {
		let timer: NodeJS.Timeout;

		if (currentTimer > 0) {
			timer = setTimeout(() => {
				const newTimer = currentTimer - 1;
				setCurrentTimer(newTimer);
			}, 1000);
		}

		return () => clearTimeout(timer);
	}, [currentTimer]);

	useEffect(() => {
		if (rateLimitRetryAfter === null) {
			setRateLimitCountdown(0);
			return;
		}

		if (rateLimitRetryAfter <= 0) {
			setRateLimitCountdown(0);
			return;
		}

		setRateLimitCountdown(rateLimitRetryAfter);
		const endTime = Date.now() + rateLimitRetryAfter * 1000;

		const timer = setInterval(() => {
			const remaining = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
			setRateLimitCountdown(remaining);

			if (remaining === 0) {
				clearInterval(timer);
			}
		}, 1000);

		return () => clearInterval(timer);
	}, [rateLimitRetryAfter]);

	const buildCodeSendMessage = () => {
		const {telefoneMascarado, emailMascarado} = savedContactInfo;
		const parts = [sendCode];
		const hasPhone = telefoneMascarado !== null;
		const hasEmail = emailMascarado !== null;

		if (hasPhone && !hasEmail) {
			parts.push(`${sendCodeToNumber}${telefoneMascarado}.`);
		}

		if (hasPhone && hasEmail) {
			parts.push(`${sendCodeToNumber}${telefoneMascarado}${sendCodeToNumberAndEmail}`);
		}

		if (hasEmail) {
			parts.push(`${sendCodeToEmail}${emailMascarado}.`);
		}

		return parts.join('');
	};

	const isResendSubmitting = requestState === OperationStateTypesEnum.PENDING;
	const isPublicResetPasswordFlow = flowType === PublicFlowEnum.FORGOT_PASSWORD && !privateFlow;
	const isRateLimitedAwaitingNewCode = rateLimitRetryAfter !== null && rateLimitCountdown === 0;
	const isResendAvailable =
		rateLimitCountdown > 0 ? false : isRateLimitedAwaitingNewCode || currentTimer === 0;
	const showErrorMessage =
		showVerifyTokenAPIError || showEmptyCodeError || (rateLimitCountdown > 0 && !!apiErrorMsg);
	const isValidButtonDisabled =
		isSubmitting || rateLimitCountdown > 0 || isRateLimitedAwaitingNewCode;
	const isResendButtonDisabled = !isResendAvailable || isSubmitting || rateLimitCountdown > 0;

	const getResendButtonLabel = () => {
		if (rateLimitCountdown > 0) {
			return `${retryAgainIn} ${formatCountdownMmSs(rateLimitCountdown, 'mmss')}`;
		}

		if (isResendAvailable) {
			return actionText.resendToken;
		}

		return `${actionText.resendToken} ${formatCountdownMmSs(currentTimer)}`;
	};

	return (
		<Fragment>
			{privateFlow && showPrivateResendToken ? (
				<Fragment>
					<Typography variant="h6" color={isLaptop ? 'textPrimary' : 'primary'}>
						{resendSecurityCode}
					</Typography>
					<ResendTokenContent
						privateFlow
						handleAction={() => {
							handleResendToken(privateFlow);
							setShowPrivateResendToken(false);
						}}
						isSubmitting={isResendSubmitting}
					/>
				</Fragment>
			) : (
				<Fragment>
					<FormProvider {...methods}>
						<Stack
							component="form"
							gap={2}
							onSubmit={handleSubmit(handleVerifyTokenSubmit)}>
							{privateFlow ? (
								<Stack spacing={2}>
									{isLaptop ? (
										<Typography variant="h6" color="textPrimary">
											{mobileSecurityCodeTitle}
										</Typography>
									) : null}
									<Typography
										variant={isLaptop ? 'subtitle1' : 'h6'}
										color={isLaptop ? 'textSecondary' : 'primary'}>
										{typeSecurityCode}
									</Typography>
								</Stack>
							) : null}
							{isPublicResetPasswordFlow ? (
								<Fragment>
									<Typography>{buildCodeSendMessage()}</Typography>
								</Fragment>
							) : null}

							<Stack>
								<Typography variant="label" component="label" htmlFor="login">
									{formLabels.securityCode}
								</Typography>
								<Stack direction="row" spacing={1}>
									{Array.from({length: VERIFICATION_CODE_LENGTH}, (_, index) => (
										<StyledInput
											{...register(VALIDATION_CODE_NAME[index + 1])}
											type="number"
											key={`code${index + 1}`}
											className={inputClass}
											onSubmit={handleSubmit(handleVerifyTokenSubmit)}
											onPaste={e => handlePaste(e, index)}
											onChange={e => {
												handleChange(e, index);
												setShowEmptyCodeError(false);
											}}
											onKeyDown={e => handleKeyDown(e, index)}
											error={showVerifyTokenAPIError || showEmptyCodeError}
											inputProps={{
												maxLength: 1,
											}}
										/>
									))}
								</Stack>
								{showErrorMessage ? (
									<Typography variant="caption" color="error" sx={{mt: 1}}>
										{showVerifyTokenAPIError ||
										(rateLimitCountdown > 0 && !!apiErrorMsg)
											? apiErrorMsg
											: invalidCode}
									</Typography>
								) : null}
							</Stack>
							<StyledFixedFooter>
								<Button
									data-tag-id={LoginEnum.CLICK_VALIDAR_CODIGO}
									size="large"
									disabled={isValidButtonDisabled}
									type="button"
									fullWidth
									onClick={handleButtonClick}
									endIcon={
										isSubmitting ? <Loading color="inherit" size={16} /> : null
									}>
									{actionText.validateToken}
								</Button>
							</StyledFixedFooter>
						</Stack>
					</FormProvider>
					<Stack gap={2}>
						<Typography>{codeDoNotSend}</Typography>
						<Button
							data-tag-id={LoginEnum.CLICK_REENVIAR_CODIGO}
							size="large"
							disabled={isResendButtonDisabled}
							type="button"
							variant={
								isResendAvailable && rateLimitCountdown === 0
									? 'outlined'
									: 'contained'
							}
							fullWidth
							onClick={() => {
								handleResendToken(privateFlow);
								resetTimer();
								setCurrentTimer(300);
								resetRateLimitState();
							}}>
							{getResendButtonLabel()}
						</Button>

						{isPublicResetPasswordFlow ? (
							<Button
								data-tag-id={LoginEnum.CLICK_PRECISO_DE_AJUDA_PAGINA}
								size="large"
								variant="outlined"
								onClick={handleHelpStep}
								fullWidth>
								{needHelp}
							</Button>
						) : null}
					</Stack>
				</Fragment>
			)}
			<Snackbar
				message={showResendTokenAPIError ? resendTokenErrorMsg : resendToken.successMessage}
				onClose={handleCloseSnackbar}
				open={openSnackbar}
			/>
			<AlertModal
				open={openRateLimitModal}
				onClose={handleCloseRateLimitModal}
				variant="info"
				headerTitle={rateLimitModalWarning}
				title={rateLimitExceededTitle}
				description={rateLimitExceededDescription}
			/>
		</Fragment>
	);
}
